'use client';

import { c as _c } from "react/compiler-runtime";
import { useQuery } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import Skeleton from 'react-loading-skeleton';
import { CertifyingLetters } from '@module/mdrt-org/shared/block-components/membership-requirements-block/components/certifying-letters/certifying-letters';
import { type MembershipRequirementsBlockWithFallbacks } from '@module/mdrt-org/shared/block-components/membership-requirements-block/utils/helpers/add-fallbacks-to-membership-requirements-block';
import { getDefaultCountryOption } from '@module/mdrt-org/shared/block-components/membership-requirements-block/utils/helpers/get-default-country-option';
import { useMembershipRequirementsOptions } from '@module/mdrt-org/shared/block-components/membership-requirements-block/utils/hooks/use-membership-requirement-options';
import { type SelectOption } from '@shared/utils/types/select-option';
import { Loader } from '../../../../components/loader/loader';
import { SelectField } from '../../../../components/select-field/select-field';
import { OrgSharedDataTag } from '../../../../utils/data/constants/tags';
import { getMembershipRequirementsPerCountry } from '../../../../utils/data/get-membership-requirements-per-country';
import { type MembershipRequirementBlock, type NextSiteSettingsPage } from '../../../../utils/data/graphql/_generated/gql-generated';
import { type MembershipRequirementDimensions, type MembershipRequirementsPerYear } from '../../../../utils/types/membership-requirements';
import { EthicalCode } from '../ethical-code/ethical-code';
import { MembershipDues } from '../membership-dues/membership-dues';
import { ProductionRequirements } from '../production-requirements/production-requirements';
import styles from './membership-requirements.module.scss';
type MembershipRequirementsProps = {
  blockData: MembershipRequirementsBlockWithFallbacks;
  dimensions: MembershipRequirementDimensions;
  siteSettingsData?: NextSiteSettingsPage;
  userCountry: {
    countryCode: string;
    countryName: string;
  };
};
export const MembershipRequirements = t0 => {
  const $ = _c(68);
  const {
    blockData,
    dimensions,
    siteSettingsData,
    userCountry
  } = t0;
  const [dropdownsReady, setDropdownsReady] = useState(false);
  const {
    countryOptions,
    yearOptions
  } = useMembershipRequirementsOptions(dimensions);
  let t1;
  if ($[0] !== countryOptions || $[1] !== userCountry) {
    t1 = getDefaultCountryOption({
      countryOptions,
      userCountry
    });
    $[0] = countryOptions;
    $[1] = userCountry;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const [selectedCountry, setSelectedCountry] = useState(t1);
  const [selectedYear, setSelectedYear] = useState(yearOptions?.[0] ?? null);
  const t2 = Boolean(selectedCountry?.value) && Boolean(selectedYear?.value);
  let t3;
  if ($[3] !== selectedCountry?.countryName || $[4] !== selectedCountry?.value) {
    t3 = () => getMembershipRequirementsPerCountry({
      countryCode: selectedCountry?.value ?? "",
      countryName: selectedCountry?.countryName ?? ""
    });
    $[3] = selectedCountry?.countryName;
    $[4] = selectedCountry?.value;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  const t4 = `${OrgSharedDataTag.GET_MEMBERSHIP_REQUIREMENTS}_${selectedCountry?.value}_${selectedCountry?.label}`;
  let t5;
  if ($[6] !== t4) {
    t5 = [OrgSharedDataTag.GET_MEMBERSHIP_REQUIREMENTS, t4];
    $[6] = t4;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  let t6;
  if ($[8] !== selectedYear?.value) {
    t6 = allYears => allYears?.requirements.find(yearData => yearData.year === selectedYear?.value);
    $[8] = selectedYear?.value;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  let t7;
  if ($[10] !== t2 || $[11] !== t3 || $[12] !== t5 || $[13] !== t6) {
    t7 = {
      enabled: t2,
      queryFn: t3,
      queryKey: t5,
      select: t6
    };
    $[10] = t2;
    $[11] = t3;
    $[12] = t5;
    $[13] = t6;
    $[14] = t7;
  } else {
    t7 = $[14];
  }
  const {
    data: countryYearData,
    error,
    isFetching
  } = useQuery(t7);
  let t8;
  let t9;
  if ($[15] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = () => {
      setDropdownsReady(true);
    };
    t9 = [];
    $[15] = t8;
    $[16] = t9;
  } else {
    t8 = $[15];
    t9 = $[16];
  }
  useEffect(t8, t9);
  let t10;
  if ($[17] !== blockData.MembershipRequirementBlocks || $[18] !== countryYearData?.year) {
    t10 = blockData.MembershipRequirementBlocks?.find(membershipRequirementBlock => new Date((membershipRequirementBlock?.ContentLink?.Expanded as MembershipRequirementBlock | undefined)?.Date).getFullYear() === countryYearData?.year)?.ContentLink?.Expanded;
    $[17] = blockData.MembershipRequirementBlocks;
    $[18] = countryYearData?.year;
    $[19] = t10;
  } else {
    t10 = $[19];
  }
  const selectedYearMembershipRequirementBlockData = t10 as MembershipRequirementBlock | undefined;
  let t11;
  if ($[20] !== blockData.ProductionRequirementsAnchorLink) {
    t11 = blockData?.ProductionRequirementsAnchorLink && {
      id: blockData.ProductionRequirementsAnchorLink
    };
    $[20] = blockData.ProductionRequirementsAnchorLink;
    $[21] = t11;
  } else {
    t11 = $[21];
  }
  let t12;
  if ($[22] !== isFetching) {
    t12 = isFetching && <Loader allowScroll={true} isInner={true} />;
    $[22] = isFetching;
    $[23] = t12;
  } else {
    t12 = $[23];
  }
  const t13 = blockData?.ProductionRequirementsStepLabel;
  let t14;
  if ($[24] !== t13) {
    t14 = <h2>{t13}</h2>;
    $[24] = t13;
    $[25] = t14;
  } else {
    t14 = $[25];
  }
  let t15;
  if ($[26] !== countryOptions || $[27] !== dropdownsReady || $[28] !== selectedCountry || $[29] !== siteSettingsData?.CountryLabel) {
    t15 = <div className={styles["header__country-selector"]}>{dropdownsReady ? <SelectField id="country-selector" isRequired={true} label={siteSettingsData?.CountryLabel ?? "Country"} name="country" onChange={value => setSelectedCountry(value as SelectOption<string> & {
        countryName: string;
      })} options={countryOptions} value={selectedCountry} withoutController={true} /> : <Skeleton className={styles["header__dropdown-skeleton"]} />}</div>;
    $[26] = countryOptions;
    $[27] = dropdownsReady;
    $[28] = selectedCountry;
    $[29] = siteSettingsData?.CountryLabel;
    $[30] = t15;
  } else {
    t15 = $[30];
  }
  let t16;
  if ($[31] !== dropdownsReady || $[32] !== selectedYear || $[33] !== siteSettingsData?.YearLabel || $[34] !== yearOptions) {
    t16 = <div className={styles["header__year-selector"]}>{dropdownsReady ? <SelectField id="year-selector" isRequired={true} label={siteSettingsData?.YearLabel ?? "Year"} name="year" onChange={value_0 => setSelectedYear(value_0 as SelectOption<number>)} options={yearOptions} value={selectedYear} withoutController={true} /> : <Skeleton className={styles["header__dropdown-skeleton"]} />}</div>;
    $[31] = dropdownsReady;
    $[32] = selectedYear;
    $[33] = siteSettingsData?.YearLabel;
    $[34] = yearOptions;
    $[35] = t16;
  } else {
    t16 = $[35];
  }
  let t17;
  if ($[36] !== t14 || $[37] !== t15 || $[38] !== t16) {
    t17 = <div className={styles.header}>{t14}{t15}{t16}</div>;
    $[36] = t14;
    $[37] = t15;
    $[38] = t16;
    $[39] = t17;
  } else {
    t17 = $[39];
  }
  let t18;
  if ($[40] !== blockData || $[41] !== countryYearData || $[42] !== error || $[43] !== isFetching || $[44] !== selectedYearMembershipRequirementBlockData) {
    t18 = <ProductionRequirements blockData={blockData} countryYearData={countryYearData} error={error} isFetching={isFetching} yearBlockData={selectedYearMembershipRequirementBlockData} />;
    $[40] = blockData;
    $[41] = countryYearData;
    $[42] = error;
    $[43] = isFetching;
    $[44] = selectedYearMembershipRequirementBlockData;
    $[45] = t18;
  } else {
    t18 = $[45];
  }
  let t19;
  if ($[46] !== t11 || $[47] !== t12 || $[48] !== t17 || $[49] !== t18) {
    t19 = <section {...t11}>{t12}{t17}{t18}</section>;
    $[46] = t11;
    $[47] = t12;
    $[48] = t17;
    $[49] = t18;
    $[50] = t19;
  } else {
    t19 = $[50];
  }
  let t20;
  if ($[51] !== blockData || $[52] !== countryYearData || $[53] !== isFetching || $[54] !== selectedYearMembershipRequirementBlockData) {
    t20 = <CertifyingLetters blockData={blockData} countryYearData={countryYearData} isFetching={isFetching} yearBlockData={selectedYearMembershipRequirementBlockData} />;
    $[51] = blockData;
    $[52] = countryYearData;
    $[53] = isFetching;
    $[54] = selectedYearMembershipRequirementBlockData;
    $[55] = t20;
  } else {
    t20 = $[55];
  }
  let t21;
  if ($[56] !== blockData) {
    t21 = <EthicalCode blockData={blockData} />;
    $[56] = blockData;
    $[57] = t21;
  } else {
    t21 = $[57];
  }
  let t22;
  if ($[58] !== blockData || $[59] !== countryYearData || $[60] !== isFetching || $[61] !== selectedYearMembershipRequirementBlockData) {
    t22 = <MembershipDues blockData={blockData} countryYearData={countryYearData} isFetching={isFetching} yearBlockData={selectedYearMembershipRequirementBlockData} />;
    $[58] = blockData;
    $[59] = countryYearData;
    $[60] = isFetching;
    $[61] = selectedYearMembershipRequirementBlockData;
    $[62] = t22;
  } else {
    t22 = $[62];
  }
  let t23;
  if ($[63] !== t19 || $[64] !== t20 || $[65] !== t21 || $[66] !== t22) {
    t23 = <div className={styles["requirements-wrapper"]}>{t19}{t20}{t21}{t22}</div>;
    $[63] = t19;
    $[64] = t20;
    $[65] = t21;
    $[66] = t22;
    $[67] = t23;
  } else {
    t23 = $[67];
  }
  return t23;
};